import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { useToast } from '_/hooks/use-toast'

import { Button, Grid } from '@mui/material'
import { Datagrid } from '@refera/ui-web'

import HeaderTitle from '_/components/header-title'

import useStyles from './styles'

import { groupColumns, TABLE_SX } from './constants'
import { navigate, useParams } from '@reach/router'
import { getGroupDataPermissionsSelector, getGroupSelector } from '_/modules/group/selectors'
import {
  getGroupById,
  getGroupDataPermissionList,
  updateGroupPermission,
} from '_/modules/group/actions'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import useRolePermission from '_/hooks/use-role-permission'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'

export function GroupPermissionsData() {
  const groupsData = useSelector(getGroupDataPermissionsSelector)
  const group = useSelector(getGroupSelector)

  const { checkIfUserDoesNotPermission } = useRolePermission()

  const { groupId } = useParams()

  const dispatch = useDispatch()
  const styles = useStyles()
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(100)
  const [orderBy, setOrderBy] = useState('permissionName')
  const [formState, setFormState] = useState([])
  const [formStateDefault, setFormStateDefault] = useState(groupsData?.results || [])

  const canEditByProfile = useCallback((row, field) => {
    const allowedMap = {
      read: 'allowedRead',
      create: 'allowedCreate',
      delete: 'allowedDelete',
    }
    const allowedField = allowedMap[field]
    if (!allowedField) return true
    return Boolean(row?.[allowedField])
  }, [])

  function handleSetValue(row, field, value) {
    if (!canEditByProfile(row, field)) {
      return
    }

    setFormState(prevState => {
      const itemIndex = prevState.findIndex(item => item.id === row?.id)
      if (itemIndex === -1) {
        // If the item doesn't exist, add it to the array
        return [
          ...prevState,
          {
            id: row?.id,
            [field]: value,
          },
        ]
      }
      // If the item exists, update it
      return prevState.map((item, index) =>
        index === itemIndex ? { ...item, [field]: value } : item
      )
    })
  }

  const { showToast } = useToast()

  const getGroupsData = useCallback(() => {
    setIsLoading(true)

    return dispatch(getGroupDataPermissionList(groupId, { pageSize, page: page + 1, orderBy }))
      .then(data => {
        setFormStateDefault(data?.results || [])
        return setIsLoading(false)
      })
      .catch(err => {
        showToast({ type: 'error', message: formatErrorMessage(err) })
        return setIsLoading(false)
      })
  }, [dispatch, pageSize, page, orderBy])

  const goBack = () => {
    window?.history?.length <= 2 ? navigate(`/`) : navigate(-1)
  }

  const handleOrderBy = useCallback(
    orderObj => {
      const order = orderObj[0]
      if (!order) {
        handleOrderBy('permissionName')
        return
      }
      const { field, sort } = order
      sort === 'desc' ? setOrderBy(`-${field}`) : setOrderBy(field)
    },
    [setOrderBy]
  )

  const handleClickSave = useCallback(() => {
    if (
      checkIfUserDoesNotPermission(PERMISSIONS.PERMISSION_GROUPS, [
        PERMISSIONS_ACTIONS.ADD,
        PERMISSIONS_ACTIONS.EDIT,
      ])
    ) {
      return
    }

    // Validação: se "Gerenciar" Alíquotas estiver desmarcado e "Gerenciar" Orçamentos estiver marcado,
    // cancelar gravação e exibir mensagem de erro (usando os nomes de campo do front com constants).
    const PERMISSION_LABELS = {
      [PERMISSIONS.TAX_RATES]: 'Alíquotas',
      [PERMISSIONS.BUDGETS]: 'Orçamentos',
    }

    // Função para pegar a linha pelo label
    const findByLabel = label => formStateDefault.find(row => row?.permissionName === label)

    // Função para pegar o valor efetivo de "Gerenciar" (create)
    const getEffectiveManage = row => {
      if (!row) return undefined
      const mod = formState.find(i => i.id === row.id)
      return mod?.create ?? row.create
    }

    const taxRatesManage = getEffectiveManage(findByLabel(PERMISSION_LABELS[PERMISSIONS.TAX_RATES]))
    const budgetsManage = getEffectiveManage(findByLabel(PERMISSION_LABELS[PERMISSIONS.BUDGETS]))

    if (!taxRatesManage && budgetsManage) {
      showToast({
        type: 'error',
        message:
          'Um usuário que não pode ver as alíquotas da empresa, também não pode editar orçamentos. Favor desmarcar "Gerenciar" "Orçamentos" ou marcar "Gerenciar" "Alíquotas".',
      })
      return
    }

    const newData = formState.map(item => {
      const newItem = { ...item }
      if (item?.create !== undefined) {
        newItem.update = item?.create
      }
      return newItem
    })

    dispatch(
      updateGroupPermission({
        groupPermissions: newData,
      })
    )
      .then(() => {
        showToast({
          type: 'success',
          message: 'Permissões salvas com sucesso',
        })
        return setIsLoading(false)
      })
      .catch(err => {
        showToast({ type: 'error', message: formatErrorMessage(err) })
        return setIsLoading(false)
      })
  }, [formState, groupId])

  function handleClickCancel() {
    setIsLoading(true)
    setFormState([])
    setFormStateDefault([])
    getGroupsData()
  }

  useEffect(() => {
    getGroupsData()
  }, [orderBy, page, pageSize])

  const getGroup = useCallback(() => {
    if (!group?.id) {
      setIsLoading(true)

      return dispatch(getGroupById(groupId))
        .then(() => {
          return setIsLoading(false)
        })
        .catch(() => {
          showToast({ type: 'error', message: 'Ocorreu um erro ao buscar os profiles.' })
          return setIsLoading(false)
        })
    }
    return null
  }, [dispatch])

  const groupName = useMemo(() => {
    const name = group?.desc

    return name ?? ''
  }, [group, groupId])

  useEffect(() => {
    getGroup()
  }, [])

  return (
    <Grid className={styles.page}>
      <HeaderTitle
        title={`Permissões de acesso a dados do Grupo ${groupName}`}
        backButtonAction={goBack}
      >
        <Grid style={{ display: 'flex', gap: '8px' }}>
          <Button className={[styles.buttonRed, styles.button]} onClick={() => handleClickCancel()}>
            Cancelar
          </Button>
          <Button className={[styles.buttonFullBlue, styles.button]} onClick={handleClickSave}>
            Salvar
          </Button>
        </Grid>
      </HeaderTitle>
      <Grid className={styles.tableContainer}>
        <Grid className={styles.mainTable}>
          <Datagrid
            initialState={{
              sorting: {
                sortModel: [{ field: 'permissionName', sort: 'asc' }],
              },
            }}
            loading={isLoading}
            rows={formStateDefault}
            columns={groupColumns(handleSetValue, formState)}
            rowCount={groupsData?.count || 0}
            sx={TABLE_SX}
            noRowsLabel="Nenhum resultado encontrado"
            paginationMode="server"
            onPageChange={setPage}
            page={page}
            onPageSizeChange={setPageSize}
            pageSize={pageSize}
            onSortModelChange={handleOrderBy}
          />
        </Grid>
      </Grid>
    </Grid>
  )
}
